import { supabase } from '@/integrations/supabase/client';
import { getCurrentUserOrganizationId } from '@/utils/tenantUtils';
import { logger } from '@/utils/logger';

export interface AuditLogEntry {
  action: string;
  entity_type: string;
  entity_id?: string;
  old_values?: Record<string, any>;
  new_values?: Record<string, any>;
  metadata?: Record<string, any>;
}

const ADMIN_ACTIVITY_ACTIONS = [
  'admin_user_added',
  'admin_setup_email_requested',
  'admin_setup_email_resent',
  'admin_setup_email_failed',
];

interface AdminActivityLogOptions {
  limit?: number;
  offset?: number;
  actions?: string[];
  targetEmail?: string;
}

interface AdminActivityLogResult {
  logs: any[];
  hasMore: boolean;
}

/**
 * Service for creating audit logs for sensitive operations
 */
export class AuditService {
  /**
   * Creates an audit log entry for a sensitive operation
   */
  static async createLog(entry: AuditLogEntry): Promise<void> {
    try {
      const organizationId = await getCurrentUserOrganizationId();
      const { data: user } = await supabase.auth.getUser();
      
      if (!user.user) {
        logger.warn('No authenticated user found for audit log');
        return;
      }

      await supabase.rpc('create_audit_log', {
        _organization_id: organizationId,
        _user_id: user.user.id,
        _action: entry.action,
        _entity_type: entry.entity_type,
        _entity_id: entry.entity_id || null,
        _old_values: entry.old_values || null,
        _new_values: entry.new_values || null,
        _metadata: entry.metadata || {}
      });
    } catch (error) {
      logger.error('Failed to create audit log:', error);
      // Don't throw - audit logging shouldn't break the main operation
    }
  }

  /**
   * Log user role changes
   */
  static async logRoleChange(
    userId: string,
    oldRole: string,
    newRole: string,
    targetUserId?: string
  ): Promise<void> {
    await this.createLog({
      action: 'role_change',
      entity_type: 'user_role',
      entity_id: targetUserId || userId,
      old_values: { role: oldRole },
      new_values: { role: newRole },
      metadata: {
        changed_by: userId,
        target_user: targetUserId || userId,
        timestamp: new Date().toISOString()
      }
    });
  }

  /**
   * Log user status changes (activation/deactivation)
   */
  static async logStatusChange(
    userId: string,
    oldStatus: string,
    newStatus: string,
    action: 'activate' | 'deactivate' | 'delete'
  ): Promise<void> {
    await this.createLog({
      action: `user_${action}`,
      entity_type: 'user_profile',
      entity_id: userId,
      old_values: { status: oldStatus },
      new_values: { status: newStatus },
      metadata: {
        action_type: action,
        timestamp: new Date().toISOString()
      }
    });
  }

  /**
   * Log profile updates (sensitive fields only)
   */
  static async logProfileUpdate(
    userId: string,
    oldValues: Record<string, any>,
    newValues: Record<string, any>
  ): Promise<void> {
    // Only log changes to sensitive fields
    const sensitiveFields = ['organization_id', 'status', 'two_factor_enabled'];
    const oldSensitive: Record<string, any> = {};
    const newSensitive: Record<string, any> = {};

    sensitiveFields.forEach(field => {
      if (oldValues[field] !== newValues[field]) {
        oldSensitive[field] = oldValues[field];
        newSensitive[field] = newValues[field];
      }
    });

    if (Object.keys(oldSensitive).length > 0) {
      await this.createLog({
        action: 'profile_update',
        entity_type: 'user_profile',
        entity_id: userId,
        old_values: oldSensitive,
        new_values: newSensitive,
        metadata: {
          fields_changed: Object.keys(oldSensitive),
          timestamp: new Date().toISOString()
        }
      });
    }
  }

  /**
   * Log authentication events
   */
  static async logAuthEvent(
    action: 'login' | 'logout' | '2fa_enabled' | '2fa_disabled' | 'password_reset',
    metadata?: Record<string, any>
  ): Promise<void> {
    const { data: user } = await supabase.auth.getUser();
    
    if (user.user) {
      await this.createLog({
        action: `auth_${action}`,
        entity_type: 'authentication',
        entity_id: user.user.id,
        metadata: {
          ...metadata,
          timestamp: new Date().toISOString(),
          user_agent: navigator.userAgent
        }
      });
    }
  }

  /**
   * Get audit logs for the current organization (admin only)
   */
  static async getAuditLogs(
    limit: number = 50,
    offset: number = 0
  ): Promise<any[]> {
    const { data, error } = await supabase
      .from('audit_logs')
      .select('*')
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    if (error) {
      throw new Error(`Failed to fetch audit logs: ${error.message}`);
    }

    const logs = data || [];
    const userIds = Array.from(new Set(logs.map((log) => log.user_id).filter(Boolean)));

    if (userIds.length === 0) return logs;

    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, full_name, email')
      .in('id', userIds);

    const profileById = new Map((profiles || []).map((profile) => [profile.id, profile]));
    return logs.map((log) => ({
      ...log,
      profiles: log.user_id ? profileById.get(log.user_id) || null : null,
    }));
  }

  /**
   * Get filtered admin onboarding activity logs for the current organization.
   */
  static async getAdminActivityLogs({
    limit = 50,
    offset = 0,
    actions = [],
    targetEmail = '',
  }: AdminActivityLogOptions = {}): Promise<AdminActivityLogResult> {
    const selectedActions = actions.length > 0 ? actions : ADMIN_ACTIVITY_ACTIONS;
    const pageLimit = Math.max(1, limit);

    let query = supabase
      .from('audit_logs')
      .select('*')
      .in('action', selectedActions)
      .order('created_at', { ascending: false })
      .range(offset, offset + pageLimit);

    const normalizedEmail = targetEmail.trim().toLowerCase();
    if (normalizedEmail) {
      query = query.ilike('metadata->>target_email', `%${normalizedEmail}%`);
    }

    const { data, error } = await query;

    if (error) {
      throw new Error(`Failed to fetch admin activity logs: ${error.message}`);
    }

    const rows = data || [];
    const hasMore = rows.length > pageLimit;
    const logs = rows.slice(0, pageLimit);
    const userIds = Array.from(new Set(logs.map((log) => log.user_id).filter(Boolean)));

    if (userIds.length === 0) return { logs, hasMore };

    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, full_name, email')
      .in('id', userIds);

    const profileById = new Map((profiles || []).map((profile) => [profile.id, profile]));
    return {
      hasMore,
      logs: logs.map((log) => ({
        ...log,
        profiles: log.user_id ? profileById.get(log.user_id) || null : null,
      })),
    };
  }
}